import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { DateTime } from "@dashboard/components/Date/DateTime";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import {
  type AtticInventoryBucketFragment,
  type AtticInventoryReportQuery,
} from "@dashboard/graphql";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import { type MessageDescriptor, useIntl } from "react-intl";

import { reportMessages, sectionMessages } from "../../messages";
import { formatCost } from "../../utils";
import { AtticInventoryNav } from "../AtticInventoryNav/AtticInventoryNav";

export type AtticInventoryReportData = NonNullable<
  AtticInventoryReportQuery["atticInventoryReport"]
>;

export interface AtticReportPageProps {
  /** undefined while loading. */
  report: AtticInventoryReportData | undefined;
  loadFailed: boolean;
  /** The CSV is being fetched. */
  downloading: boolean;
  onDownloadCsv: () => void;
}

const Figure = ({ label, value, testId }: { label: string; value: string; testId: string }) => (
  <Box display="flex" flexDirection="column" gap={1} __minWidth="110px">
    <Text size={2} color="default2">
      {label}
    </Text>
    <Text size={6} fontWeight="medium" data-test-id={testId}>
      {value}
    </Text>
  </Box>
);

const numberOfColumns = 5;

/** One breakdown as a card with a table: label, SKUs, on hand, cost, retail. */
const Breakdown = ({
  id,
  title,
  firstColumn,
  rows,
  currency,
}: {
  id: string;
  title: MessageDescriptor;
  firstColumn: MessageDescriptor;
  /** undefined while loading. */
  rows: AtticInventoryBucketFragment[] | undefined;
  currency: string;
}) => {
  const intl = useIntl();

  return (
    <DashboardCard data-test-id={`attic-report-${id}`}>
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(title)}</DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content paddingX={0}>
        <ResponsiveTable>
          <TableHead>
            <TableRowLink>
              <TableCell style={{ width: "100%" }}>{intl.formatMessage(firstColumn)}</TableCell>
              <TableCell align="right">{intl.formatMessage(reportMessages.skus)}</TableCell>
              <TableCell align="right">{intl.formatMessage(reportMessages.onHand)}</TableCell>
              <TableCell align="right">{intl.formatMessage(reportMessages.costValue)}</TableCell>
              <TableCell align="right">{intl.formatMessage(reportMessages.retailValue)}</TableCell>
            </TableRowLink>
          </TableHead>
          <TableBody>
            {renderCollection(
              rows,
              row => (
                <TableRowLink
                  key={row ? row.key || "other" : "skeleton"}
                  data-test-id={`attic-report-${id}-row`}
                >
                  <TableCell>{row ? <Text size={3}>{row.label}</Text> : <Skeleton />}</TableCell>
                  <TableCell align="right">
                    {row ? <Text size={3}>{row.skus}</Text> : <Skeleton />}
                  </TableCell>
                  <TableCell align="right">
                    {row ? <Text size={3}>{row.onHand}</Text> : <Skeleton />}
                  </TableCell>
                  <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                    {row ? (
                      <Text size={3}>{formatCost(row.costValue, currency, intl)}</Text>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                    {row ? (
                      <Text size={3}>{formatCost(row.retailValue, currency, intl)}</Text>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRowLink>
              ),
              () => (
                <TableRowLink data-test-id={`attic-report-${id}-empty`}>
                  <TableCell colSpan={numberOfColumns}>
                    <Text size={3} color="default2">
                      {intl.formatMessage(reportMessages.empty)}
                    </Text>
                  </TableCell>
                </TableRowLink>
              ),
            )}
          </TableBody>
        </ResponsiveTable>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export const AtticReportPage = ({
  report,
  loadFailed,
  downloading,
  onDownloadCsv,
}: AtticReportPageProps) => {
  const intl = useIntl();
  const currency = report?.currency ?? "USD";
  const totals = report?.totals;
  const figure = (value: number | undefined) =>
    value === undefined ? "" : intl.formatNumber(value);
  const money = (value: number | undefined) =>
    value === undefined ? "" : formatCost(value, currency, intl);

  return (
    <ListPageLayout>
      <TopNav
        title={intl.formatMessage(sectionMessages.report)}
        subtitle={
          <Text size={2} color="default2">
            {intl.formatMessage(reportMessages.subtitle)}
          </Text>
        }
      >
        <Button
          variant="secondary"
          disabled={!report || downloading || report.totals.onHand === 0}
          onClick={onDownloadCsv}
          data-test-id="attic-report-download"
        >
          {intl.formatMessage(reportMessages.downloadCsv)}
        </Button>
      </TopNav>
      <AtticInventoryNav current="report" />
      <Box display="flex" flexDirection="column" gap={4} paddingBottom={6}>
        <DashboardCard data-test-id="attic-report-totals">
          <DashboardCard.Content>
            {loadFailed ? (
              <Text as="p" size={3} color="critical1" data-test-id="attic-report-failed">
                {intl.formatMessage(sectionMessages.loadFailed)}
              </Text>
            ) : (
              <>
                <Box display="grid" gridTemplateColumns={{ mobile: 2, desktop: 4 }} gap={5}>
                  <Figure
                    label={intl.formatMessage(reportMessages.skus)}
                    value={figure(totals?.skus)}
                    testId="attic-report-skus"
                  />
                  <Figure
                    label={intl.formatMessage(reportMessages.onHand)}
                    value={figure(totals?.onHand)}
                    testId="attic-report-on-hand"
                  />
                  <Figure
                    label={intl.formatMessage(reportMessages.reserved)}
                    value={figure(totals?.reserved)}
                    testId="attic-report-reserved"
                  />
                  <Figure
                    label={intl.formatMessage(reportMessages.sellable)}
                    value={figure(totals?.sellable)}
                    testId="attic-report-sellable"
                  />
                  <Figure
                    label={intl.formatMessage(reportMessages.lots)}
                    value={figure(totals?.lots)}
                    testId="attic-report-lots"
                  />
                  <Figure
                    label={intl.formatMessage(reportMessages.locations)}
                    value={figure(totals?.locations)}
                    testId="attic-report-locations"
                  />
                  <Figure
                    label={intl.formatMessage(reportMessages.costValue)}
                    value={money(totals?.costValue)}
                    testId="attic-report-cost"
                  />
                  <Figure
                    label={intl.formatMessage(reportMessages.retailValue)}
                    value={money(totals?.retailValue)}
                    testId="attic-report-retail"
                  />
                </Box>
                {report && (
                  <Text
                    as="p"
                    size={2}
                    color="default2"
                    marginTop={4}
                    data-test-id="attic-report-as-of"
                  >
                    {intl.formatMessage(reportMessages.generatedAt, {
                      when: <DateTime date={report.generatedAt} plain />,
                    })}
                  </Text>
                )}
              </>
            )}
          </DashboardCard.Content>
        </DashboardCard>
        {!loadFailed && (
          <>
            <Breakdown
              id="by-set"
              title={reportMessages.bySet}
              firstColumn={reportMessages.set}
              rows={report?.bySet}
              currency={currency}
            />
            <Breakdown
              id="by-condition"
              title={reportMessages.byCondition}
              firstColumn={reportMessages.condition}
              rows={report?.byCondition}
              currency={currency}
            />
            <Breakdown
              id="by-location"
              title={reportMessages.byLocation}
              firstColumn={reportMessages.location}
              rows={report?.byLocation}
              currency={currency}
            />
          </>
        )}
      </Box>
    </ListPageLayout>
  );
};

export default AtticReportPage;
